import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { filter, map, take } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { BUTTON_CLICK } from 'src/app/components/shared/modal/exit-modal/exit-modal.constants';
import {
  IMAGE_SRC,
  VALIDATION_MESSAGE,
} from 'src/app/components/shared/shared-constants';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpecialCharacters } from 'src/app/core/enums';
import { UserAdmin } from 'src/app/core/models/admin';
import {
  GetLocationProfile,
  LocationGroup,
  LocationProfile,
} from 'src/app/core/models/admin/location-admin.model';
import { LocationService } from 'src/app/core/services/admin/location/location.service';
import { UserService } from 'src/app/core/services/user/user.service';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { LocationProfilePopupComponent } from '../../location-admin/location-profile-popup/location-profile-popup.component';
import {
  ColumnIds,
  ColumnText,
  DISPLAY_TEXT,
  GROUP_WARNING_MODAL,
  LinkedSite,
  LocationGroupTableData,
} from '../location-group.constants';
import { CONST, DISPLAYED_COLS, TABLE_COLS } from './linked-sites.constants';

@Component({
  selector: 'app-linked-sites',
  templateUrl: './linked-sites.component.html',
  styleUrls: ['./linked-sites.component.scss'],
  standalone: false,
})
export class LinkedSitesComponent implements OnInit, OnChanges {
  @Input() group!: LocationGroupTableData;
  @Input() isDeleteDisabled = true;
  @Output() linkSiteChanges = new EventEmitter();

  readonly destroy = inject(DestroyRef);
  private userService = inject(UserService);

  groupReturnState!: {
    code: string;
    name: string;
  };

  readonly CONSTANTS = {
    ...DISPLAY_TEXT,
    ...VALIDATION_MESSAGE,
    ...GROUP_WARNING_MODAL,
    ...CONST,
  };

  readonly COLUMNTEXT = ColumnText;
  readonly IMAGES = IMAGE_SRC;
  readonly DISPLAYED_COLS = DISPLAYED_COLS;
  readonly TABLE_COLUMNS = TABLE_COLS;
  readonly SpecialCharacters = SpecialCharacters;

  sortAddedAsc = false;

  dataSource!: MatTableDataSource<LocationGroupTableData>;
  linkedSiteDataSource!: MatTableDataSource<LinkedSite>;
  isEditMode = false;
  isTextChanged = false;
  isUndoing = false;
  activeTextBox = '';

  sitesData = this.commonUtils.createTableColumn(ColumnIds.GROUP);
  slicData = this.commonUtils.createTableColumn(ColumnIds.SLIC);
  originalLinkSitesTable: LinkedSite[] = [];

  currentUser!: UserAdmin;

  constructor(
    private dialog: MatDialog,
    public readonly commonUtils: CommonUtils,
    private locationService: LocationService,
  ) {}

  ngOnInit(): void {
    this.createReturnState(this.group);
    this.userService
      .getCurrentUser$()
      .pipe(
        takeUntilDestroyed(this.destroy),
        filter((user): user is UserAdmin => !!user),
        map((user) => {
          this.currentUser = user;
          const groupData = [{ ...this.group }];
          this.dataSource = new MatTableDataSource(groupData);
          this.linkedSiteDataSource = new MatTableDataSource(
            this.group?.linkedSites,
          );
          this.originalLinkSitesTable = this.group?.linkedSites;
          this.linkedSiteDataSource.filterPredicate =
            this.commonUtils.createFilter();
        }),
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.group?.firstChange && changes.group?.currentValue) {
      this.dataSource.data = [{ ...changes.group.currentValue }];
      this.linkedSiteDataSource.data = [
        ...changes.group.currentValue.linkedSites,
      ];
      this.originalLinkSitesTable = changes.group.currentValue.linkedSites;
      this.isEditMode = false;
      this.createReturnState(changes.group.currentValue);
    }
  }

  clearAllFilters() {
    this.commonUtils.clearAllFilters(
      this.linkedSiteDataSource,
      this.originalLinkSitesTable,
      [this.sitesData.formControl, this.slicData.formControl],
    );
  }

  createReturnState(group: LocationGroupTableData) {
    this.isTextChanged = false;
    this.groupReturnState = {
      code: group?.code || '',
      name: group?.name || '',
    };
  }

  onCodeChange(change: string) {
    this.isTextChanged = this.groupReturnState.code != change;
  }

  onNameChange(change: string) {
    this.isTextChanged = this.groupReturnState.name != change;
  }

  clearText(field: keyof LocationGroupTableData, index: number) {
    this.dataSource.data[index][field] = '' as never;
  }

  save(group: LocationGroupTableData) {
    if (this.isUndoing || !this.isTextChanged) return;

    this.isEditMode = false;
    const payload = {
      id: group.id,
      version: group.version,
      code: group.code,
      name: group.name,
    };

    this.locationService
      .updateLocationGroup(this.group.code, payload)
      .pipe(take(1))
      .subscribe((res) => {
        if (
          res?.code === 200 &&
          res?.response === 'Location Group updated successfully'
        ) {
          this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
          this.linkSiteChanges.emit();

          this.group = {
            ...this.group,
            code: payload.code,
            name: payload.name,
          };

          this.dataSource = new MatTableDataSource([{ ...this.group }]);

          this.createReturnState(this.group);
          this.isTextChanged = false;
          this.isUndoing = false;
        }
      });
  }

  refresh() {
    if (this.isUndoing) return;
    this.isEditMode = false;
    this.dataSource.data = [{ ...this.group }];
  }

  async undo(): Promise<void> {
    if (this.isUndoing) return;
    this.isUndoing = true;

    try {
      const restored: LocationGroupTableData = {
        ...this.group,
        code: this.groupReturnState.code,
        name: this.groupReturnState.name,
      };

      this.dataSource.data = [restored];
      this.isTextChanged = false;

      await new Promise((res) => setTimeout(res, 0));
    } catch (e) {
      (console.error('Undo failed'), e);
    } finally {
      this.isUndoing = false;
    }
  }

  onDelete(groupName: string, index: number) {
    this.commonUtils
      .openWarningModal({
        header: this.CONSTANTS.HEADER,
        body: this.CONSTANTS.BODY,
        extraText: groupName,
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp.button === BUTTON_CLICK.ACCEPT) {
          this.locationService
            .deleteLocationGroup(
              this.dataSource.data[index] as unknown as LocationGroup,
              this.currentUser?.loginId,
            )
            .pipe(take(1))
            .subscribe(() => {
              this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
              this.linkSiteChanges.emit();
            });
        }
      });
  }

  locationProfile(data: LocationGroupTableData) {
    if (data?.code) {
      this.locationService
        .getLocationProfile(data.code)
        .pipe(take(1))
        .subscribe((locationInfo: GetLocationProfile) => {
          this.openProfile(locationInfo.response);
        });
    }
  }

  openProfile(locationProfile: LocationProfile) {
    this.dialog.open(LocationProfilePopupComponent, {
      disableClose: true,
      width: '57rem',
      maxHeight: '95vh',
      data: { profile: locationProfile, edit: true },
    });
  }

  search() {
    this.commonUtils.searchFilter(
      [this.sitesData, this.slicData],
      this.linkedSiteDataSource,
    );
  }

  preventSpecialCharacters(event: KeyboardEvent): boolean {
    return this.commonUtils.preventSpecialCharacters(
      event,
      SpecialCharacters.LINKED_SITES,
    );
  }
}
